import { isEmpty } from 'lodash-es';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import {
  EventAccordionItemProps,
  translateEventToInventoryAccordionItem,
} from 'src/components/Accordions';
import { SwiperButton } from 'src/components/Buttons/SwiperButton';
import { BackSection } from 'src/components/common/BackSection';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { StubHubListingManualPricingContextProvider } from 'src/components/Listings/InventoryEventPage/MarketListings/StubHubListingsManualPricingContext';
import { ActiveFocusContextProvider } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { CollapsableViewProvider } from 'src/contexts/CollapsableViewContext/CollapsableViewContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext/EventHubContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { InputPriceFocusProvider } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  EventLastReviewedContextProvider,
  useEventLastReviewedContext,
} from 'src/contexts/SellerEventLastReviewContext/SellerEventLastReviewContext';
import {
  SidePanelPage,
  SidePanelProvider,
} from 'src/contexts/SidePanelContext';
import { ExplorerLayout } from 'src/core/layouts';
import { LoadingButton } from 'src/core/POS/LoadingButton/LoadingButton';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useSellerEventNote } from 'src/hooks/api/useSellerEventNote';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useQuickFilterSearchParam } from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { InventoryDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getAppliedFilterCounts } from 'src/utils/eventQueryUtils';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import {
  EventWithData,
  Feature,
  Listing,
  ListingDetailDataField,
  ListingDetails,
  ListingQuery,
} from 'src/WebApiController';
import Swiper from 'swiper';

import { InventoryEventCatchUpBar } from './InventoryEventCatchUp';
import { useCatchUpModeEventCards } from './InventoryEventCatchUp/InventoryEventCatchUpBar.hooks';
import * as styles from './InventoryEventPage.css';
import { useCatchUpParams } from './InventoryEventPage.hooks';
import { InventoryEventPageContent } from './InventoryEventPageContent';
import { InventoryEventPageHeader } from './InventoryEventPageHeader';
import { InventoryEventPageRightPanel } from './InventoryEventPageRightPanel';
import { InventoryEventTabs } from './InventoryEventTabs';

export function InventoryEventPage({
  viagVirtId,
  posEventIds,
  returnUrl,
}: {
  posEventIds?: string[];
  viagVirtId?: string;
  returnUrl?: string;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasPricingCatchupFeature = useUserHasFeature(Feature.PricingCatchup);
  const {
    eventsTransformed,
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  useEffect(() => {
    if (!viagVirtId && posEventIds?.length) {
      setListItemExpansion(
        true,
        posEventIds.map((id) => `old:${id}`)
      );
    } else if (viagVirtId) {
      setListItemExpansion(true, [viagVirtId]);
    }
  }, [
    activeAccountWebClientConfig,
    posEventIds,
    setListItemExpansion,
    viagVirtId,
  ]);

  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const accordionItemProps = useMemo(
    () =>
      eventsTransformed?.length
        ? eventsTransformed.find(
            (ev) =>
              ev.event.virtId === viagVirtId ||
              ev.event.posIds.some((id) => posEventIds?.includes(id))
          )
        : undefined,
    [eventsTransformed, posEventIds, viagVirtId]
  );

  const inventoryAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToInventoryAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (
      inventoryAccordionItem &&
      !isEqual(allGroupsIds, [inventoryAccordionItem.itemId])
    ) {
      setAllGroupsIds([inventoryAccordionItem.itemId]);
    }
  }, [allGroupsIds, inventoryAccordionItem, setAllGroupsIds]);

  return (
    <SidePanelProvider sidePanelId="inventory-event">
      <ActiveFocusContextProvider>
        <EventMapContextProvider event={accordionItemProps?.event}>
          <InputPriceFocusProvider
            disablePagination
            pageSize={accordionItemProps?.counts?.listCnt ?? 0}
          >
            <StubHubListingManualPricingContextProvider
              groupId={accordionItemProps?.event.virtId}
            >
              <EventLastReviewedContextProvider>
                <Stack direction="column" height="full">
                  <InventoryEventContent
                    accordionItemProps={accordionItemProps}
                    inventoryAccordionItem={inventoryAccordionItem}
                    returnUrl={returnUrl}
                  />
                  {hasPricingCatchupFeature && <InventoryEventCatchUpBar />}
                </Stack>
              </EventLastReviewedContextProvider>
            </StubHubListingManualPricingContextProvider>
          </InputPriceFocusProvider>
        </EventMapContextProvider>
      </ActiveFocusContextProvider>
    </SidePanelProvider>
  );
}

type InventoryEventContentProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  returnUrl?: string;
};

function InventoryEventContent({
  accordionItemProps,
  inventoryAccordionItem,
  returnUrl,
}: InventoryEventContentProps) {
  const [selectedListingIds, setSelectedListingIds] = useState<number[]>([]);

  const { isLoading } = useCatalogDataContext();

  const isMobile = useMatchMedia('mobile');

  const quickFilterSearchParams = useQuickFilterSearchParam();
  const { tempQuery, initialQuery } = useFilterQueryContext<ListingQuery>();

  const { setModal } = useContext(ModalContext);
  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();

  const hasconsignorMasterUI = useUserHasConsignorMasterUI();

  const hasPricingV2DemandDashboard = useUserHasFeature(
    Feature.PricingV2DemandDashboard
  );
  const intialActiveTab =
    hasPricingV2DemandDashboard && !hasconsignorMasterUI
      ? InventoryEventTabs.Dashboard
      : InventoryEventTabs.Info;

  const [activeTab, setActiveTab] = useState(intialActiveTab);

  const activeId = getDeepLinkIdFromUrl(
    InventoryDeeplinkQueryParam,
    window.location.href
  );

  useEffect(() => {
    if (activeId?.length && !listing) {
      setActivePosEntity(parseInt(activeId), undefined, true, [
        ListingDetailDataField.Basic,
      ]);
      setModal(getListingDetailsModalConfigWithDeepLink(isMobile, activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const swiperRef = useRef<Swiper>();

  const event = accordionItemProps?.event;

  const {
    isCompleted,
    isLoading: isEventReviewedLoading,
    onSettingVirtualIds,
    onUpdateLastReviewedDateTime,
  } = useEventLastReviewedContext();

  useEffect(() => {
    onSettingVirtualIds(event?.virtId ? [event?.virtId] : []);
  }, [event?.virtId, onSettingVirtualIds]);

  const { addNote, isLoading: isEventNoteLoading } = useSellerEventNote(
    event?.viagId
  );
  const markAsReviewed = useContent(ContentId.MarkAsReviewed);
  const markedAsReviewed = useContent(ContentId.MarkedAsReviewed);

  const { isCatchupMode } = useCatchUpParams();
  const { onNextClick } = useCatchUpModeEventCards();

  const onMarkAsReviewed = useCallback(async () => {
    if (event?.virtId) {
      await addNote(markedAsReviewed);
      onUpdateLastReviewedDateTime(event.virtId);
      onNextClick();
    }
  }, [
    addNote,
    event?.virtId,
    markedAsReviewed,
    onNextClick,
    onUpdateLastReviewedDateTime,
  ]);

  const {
    onSelectListings,
    onUpdateInventoryTab,
    selectedListingIds: companionModeSelectedListingIds,
    isOnSidePanel,
    inventoryTab,
    enabled,
    sessionRole,
  } = useEventHubContext();

  const onSetInventoryTab = useCallback(
    (tab: InventoryEventTabs) => {
      setActiveTab(tab);
      if (enabled && !isOnSidePanel) {
        onUpdateInventoryTab(tab);
      }
    },
    [enabled, isOnSidePanel, onUpdateInventoryTab]
  );

  useEffect(() => {
    if (isOnSidePanel && inventoryTab) {
      setActiveTab(inventoryTab);
    }
  }, [activeTab, enabled, inventoryTab, isOnSidePanel]);

  useEffect(() => {
    if (isOnSidePanel && companionModeSelectedListingIds) {
      if (!isEqual(companionModeSelectedListingIds, selectedListingIds)) {
        setSelectedListingIds(companionModeSelectedListingIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companionModeSelectedListingIds, isOnSidePanel]);

  useEffect(() => {
    // do nothing when not in companion mode
    if (!(enabled && sessionRole && !isOnSidePanel)) {
      return;
    }

    if (isEmpty(selectedListingIds)) {
      return;
    }

    onSelectListings(selectedListingIds);
  }, [
    enabled,
    isOnSidePanel,
    onSelectListings,
    selectedListingIds,
    sessionRole,
  ]);

  if (!event) {
    if (
      quickFilterSearchParams ||
      getAppliedFilterCounts(tempQuery, initialQuery)
    )
      return (
        <EmptySectionContent.Root
          icon={
            <EmptySectionContent.SolidIconContainer>
              <SearchSolidIcon />
            </EmptySectionContent.SolidIconContainer>
          }
        >
          <EmptySectionContent.Label>
            <Content id={ContentId.NoResultFound} />
          </EmptySectionContent.Label>
          <EmptySectionContent.DetailMessage>
            <Content id={ContentId.NoFilteredNotifications} />
          </EmptySectionContent.DetailMessage>
        </EmptySectionContent.Root>
      );
  }

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <div className={styles.root}>
      <ExplorerLayout
        swiperRef={swiperRef}
        defaultRatio={0.35}
        maxPanelPercent={0.5}
        minPanelSize={400}
        showExpandButton
        sidePanelPage={SidePanelPage.InventoryEvent}
        rightPanelContent={
          !(enabled && sessionRole) && (
            <InventoryEventPageRightPanel
              event={event}
              listings={accordionItemProps.entities.listings}
              selectedListingIds={selectedListingIds}
              activeTab={activeTab}
            />
          )
        }
      >
        <div className={styles.leftContainer}>
          {isMobile && (
            <Stack
              justifyContent="spaceBetween"
              alignItems="center"
              style={{
                padding: `0 ${vars.spacing['xl']} ${vars.spacing['lg']} ${vars.spacing['xl']}`,
              }}
            >
              <BackSection
                returnUrl={returnUrl ?? '/inventory'}
                returnState={{
                  fromViagVirtId: event.virtId,
                }}
              />
              <SwiperButton dir="right" />
            </Stack>
          )}
          <CollapsableViewProvider isEnabled={true}>
            <InventoryEventPageHeader
              event={event}
              inventoryAccordionItem={inventoryAccordionItem}
            />
            {isOnSidePanel ? (
              <InventoryEventPageRightPanel
                event={event}
                listings={accordionItemProps?.entities?.listings}
                selectedListingIds={selectedListingIds}
                activeTab={activeTab}
              />
            ) : (
              <InventoryEventPageContent
                accordionItemProps={accordionItemProps}
                inventoryAccordionItem={inventoryAccordionItem}
                selectedListingIds={selectedListingIds}
                setSelectedListingIds={setSelectedListingIds}
                activeTab={activeTab}
                setActiveTab={onSetInventoryTab}
              />
            )}
          </CollapsableViewProvider>
          {isCatchupMode && (
            <div className={styles.absoluteButton}>
              <LoadingButton
                title={markAsReviewed}
                isLoading={isEventReviewedLoading || isEventNoteLoading}
                isCompleted={isCompleted}
                onClick={onMarkAsReviewed}
              />
            </div>
          )}
        </div>
      </ExplorerLayout>
    </div>
  );
}
